// Copyright 2022, Imprivata, Inc.  All rights reserved.
import { useCallback, useEffect, useState } from 'react';
import { getCachedSvg, preloadSvg } from '../../utils/SvgPreloader';
interface ScriptedSvgProps {
  url: string;
  className?: string;
  onLoad?: () => void;
  onError?: (error: Error) => void;
}
const ScriptedSvg: React.FC<ScriptedSvgProps> = ({ url, className, onLoad, onError }) => {
  const [svgElement, setSvgElement] = useState<SVGSVGElement | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const parseSvgElement = useCallback((svgString: string): SVGSVGElement | null => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgString, 'image/svg+xml');
    return doc.querySelector('svg');
  }, []);
  const executeSvgScript = useCallback(
    async (svgString: string): Promise<void> => {
      const scriptRegex = /<script><!\[CDATA\[(.*?)\]\]><\/script>/gs;
      const matches = scriptRegex.exec(svgString);
      const svgScript = matches?.[1] ?? '';
      try {
        const scriptFunction = new Function(svgScript);
        scriptFunction();
      } catch (error) {
        console.error('Error executing SVG script:', error);
        onError?.(error as Error);
      }
    },
    [onError],
  );
  useEffect(() => {
    let isMounted = true;
    // Check if SVG is already cached
    const cachedPromise = getCachedSvg(url);
    const loadPromise = cachedPromise || preloadSvg(url);
    loadPromise
      .then((svgString) => {
        if (!isMounted) {
          return;
        }
        const parsedSvg = parseSvgElement(svgString);
        if (parsedSvg) {
          if (className) {
            parsedSvg.classList.add(className);
          }
          setSvgElement(parsedSvg);
          setIsLoading(false);
          onLoad?.();
        }
      })
      .catch((error) => {
        if (isMounted) {
          console.error('Error loading SVG:', error);
          setIsLoading(false);
          onError?.(error);
        }
      });
    return () => {
      isMounted = false;
    };
  }, [url, className, parseSvgElement, onLoad, onError]);
  useEffect(() => {
    if (svgElement && !isLoading) {
      executeSvgScript(svgElement.outerHTML);
    }
  }, [svgElement, isLoading, executeSvgScript]);
  return (
    <div>
      <div
        ref={(node) => {
          if (node && svgElement) { node.replaceWith(svgElement); }
        }}
      />
    </div>
  );
};
export default ScriptedSvg;
